import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Receipt, Search, Filter, Eye, Check, CreditCard, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  'brouillon': 'bg-gray-100 text-gray-800',
  'validee': 'bg-blue-100 text-blue-800',
  'payee': 'bg-green-100 text-green-800',
  'annulee': 'bg-red-100 text-red-800'
};

const statusLabels = {
  'brouillon': 'Brouillon',
  'validee': 'Validée',
  'payee': 'Payée',
  'annulee': 'Annulée'
};

const categories = [
  { value: 'administration', label: 'Administration' },
  { value: 'assurance', label: 'Assurance' },
  { value: 'entretien_parties_communes', label: 'Entretien parties communes' },
  { value: 'honoraires_syndic', label: 'Honoraires syndic' },
  { value: 'ascenseur', label: 'Ascenseur' },
  { value: 'chauffage', label: 'Chauffage' },
  { value: 'eau_froide', label: 'Eau froide' },
  { value: 'eau_chaude', label: 'Eau chaude' },
  { value: 'espaces_verts', label: 'Espaces verts' },
  { value: 'travaux_entretien', label: 'Travaux entretien' },
  { value: 'travaux_amelioration', label: 'Travaux amélioration' },
  { value: 'autres', label: 'Autres' }
];

export default function FacturesSection({ buildingId, exerciceId, token }) {
  const [factures, setFactures] = useState([]);
  const [fournisseurs, setFournisseurs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedFacture, setSelectedFacture] = useState(null);

  const fetchFactures = useCallback(async () => {
    try {
      let url = `${API_URL}/api/comptabilite/factures?building_id=${buildingId}`;
      if (exerciceId) url += `&exercice_id=${exerciceId}`;
      if (statusFilter) url += `&statut=${statusFilter}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFactures(data);
      }
    } catch (error) {
      console.error('Error fetching factures:', error);
    } finally {
      setLoading(false);
    }
  }, [buildingId, exerciceId, statusFilter, token]);

  const fetchFournisseurs = useCallback(async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/comptabilite/fournisseurs?building_id=${buildingId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setFournisseurs(data);
      }
    } catch (error) {
      console.error('Error fetching fournisseurs:', error);
    }
  }, [buildingId, token]);

  useEffect(() => {
    fetchFactures();
    fetchFournisseurs();
  }, [fetchFactures, fetchFournisseurs]);

  const handleValider = async (factureId) => {
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/factures/${factureId}/valider`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        toast.success('Facture validée');
        fetchFactures();
      }
    } catch (error) {
      toast.error('Erreur lors de la validation');
    }
  };

  const handlePayer = async (factureId) => {
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/factures/${factureId}/payer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          date_paiement: new Date().toISOString().split('T')[0],
          mode_paiement: 'virement'
        })
      });
      if (response.ok) {
        toast.success('Facture marquée comme payée');
        fetchFactures();
      }
    } catch (error) {
      toast.error('Erreur lors du paiement');
    }
  };

  const filteredFactures = factures.filter(f => 
    f.fournisseur_nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    f.numero?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const totalTTC = filteredFactures.reduce((sum, f) => sum + (f.montant_ttc || 0), 0);
  const totalPaye = filteredFactures.filter(f => f.statut === 'payee').reduce((sum, f) => sum + (f.montant_ttc || 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Factures</h2>
          <p className="text-sm text-gray-500">
            {filteredFactures.length} facture(s) • Total: {formatMoney(totalTTC)} • Payé: {formatMoney(totalPaye)}
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6]"
        >
          <Plus className="h-5 w-5" />
          Nouvelle facture
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher par fournisseur ou numéro..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-200 rounded-lg bg-white"
        >
          <option value="">Tous les statuts</option>
          <option value="brouillon">Brouillon</option>
          <option value="validee">Validée</option>
          <option value="payee">Payée</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00D3A8]"></div>
          </div>
        ) : filteredFactures.length === 0 ? (
          <div className="text-center py-12">
            <Receipt className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucune facture</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Numéro</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fournisseur</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Montant TTC</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredFactures.map(facture => (
                <tr key={facture.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{facture.numero}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">{facture.fournisseur_nom}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {new Date(facture.date_facture).toLocaleDateString('fr-FR')}
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-medium">{formatMoney(facture.montant_ttc)}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColors[facture.statut]}`}>
                      {statusLabels[facture.statut]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      {facture.statut === 'brouillon' && (
                        <button
                          onClick={() => handleValider(facture.id)}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg"
                          title="Valider"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      )}
                      {facture.statut === 'validee' && (
                        <button
                          onClick={() => handlePayer(facture.id)}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded-lg"
                          title="Marquer payée"
                        >
                          <CreditCard className="h-4 w-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setSelectedFacture(facture)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                        title="Voir détails"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <CreateFactureModal
          buildingId={buildingId}
          exerciceId={exerciceId}
          fournisseurs={fournisseurs}
          token={token}
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            fetchFactures();
          }}
        />
      )}

      {/* Detail Modal */}
      {selectedFacture && (
        <FactureDetailModal
          facture={selectedFacture}
          onClose={() => setSelectedFacture(null)}
        />
      )}
    </div>
  );
}

// Create Facture Modal
function CreateFactureModal({ buildingId, exerciceId, fournisseurs, token, onClose, onCreated }) {
  const [formData, setFormData] = useState({
    fournisseur_id: '',
    date_facture: new Date().toISOString().split('T')[0],
    numero_fournisseur: '',
    lignes: [{ description: '', quantite: 1, prix_unitaire_ht: 0, tva_taux: 20, categorie: 'autres' }],
    notes: ''
  });
  const [saving, setSaving] = useState(false);
  const [showNewFournisseur, setShowNewFournisseur] = useState(false);
  const [newFournisseur, setNewFournisseur] = useState({ nom: '', email: '', telephone: '' });

  const handleAddLigne = () => {
    setFormData({
      ...formData,
      lignes: [...formData.lignes, { description: '', quantite: 1, prix_unitaire_ht: 0, tva_taux: 20, categorie: 'autres' }]
    });
  };

  const handleRemoveLigne = (index) => {
    const newLignes = [...formData.lignes];
    newLignes.splice(index, 1);
    setFormData({ ...formData, lignes: newLignes });
  };

  const handleLigneChange = (index, field, value) => {
    const newLignes = [...formData.lignes];
    newLignes[index][field] = value;
    setFormData({ ...formData, lignes: newLignes });
  };

  const calculateTotals = () => {
    let totalHT = 0;
    let totalTVA = 0;
    formData.lignes.forEach(ligne => {
      const ht = (ligne.quantite || 0) * (ligne.prix_unitaire_ht || 0);
      const tva = ht * (ligne.tva_taux || 0) / 100;
      totalHT += ht;
      totalTVA += tva;
    });
    return { totalHT, totalTVA, totalTTC: totalHT + totalTVA };
  };

  const handleCreateFournisseur = async () => {
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/fournisseurs`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...newFournisseur, building_id: buildingId })
      });
      if (response.ok) {
        const data = await response.json();
        toast.success('Fournisseur créé');
        setFormData({ ...formData, fournisseur_id: data.fournisseur_id });
        setShowNewFournisseur(false);
        // Refresh fournisseurs list would be nice here
      }
    } catch (error) {
      toast.error('Erreur lors de la création du fournisseur');
    }
  };

  const handleSubmit = async () => {
    if (!formData.fournisseur_id) {
      toast.error('Sélectionnez un fournisseur');
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/comptabilite/factures`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          building_id: buildingId,
          exercice_id: exerciceId,
          ...formData
        })
      });

      if (response.ok) {
        toast.success('Facture créée');
        onCreated();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  const { totalHT, totalTVA, totalTTC } = calculateTotals();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold">Nouvelle facture</h3>
        </div>
        
        <div className="p-6 overflow-y-auto max-h-[60vh] space-y-6">
          {/* Fournisseur */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Fournisseur *</label>
            <div className="flex gap-2">
              <select
                value={formData.fournisseur_id}
                onChange={(e) => setFormData({ ...formData, fournisseur_id: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-lg"
              >
                <option value="">Sélectionner un fournisseur</option>
                {fournisseurs.map(f => (
                  <option key={f.id} value={f.id}>{f.nom}</option>
                ))}
              </select>
              <button
                onClick={() => setShowNewFournisseur(true)}
                className="px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                <Plus className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Date et référence */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date facture *</label>
              <input
                type="date"
                value={formData.date_facture}
                onChange={(e) => setFormData({ ...formData, date_facture: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">N° facture fournisseur</label>
              <input
                type="text"
                value={formData.numero_fournisseur}
                onChange={(e) => setFormData({ ...formData, numero_fournisseur: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                placeholder="Ex: FAC-2025-001"
              />
            </div>
          </div>

          {/* Lignes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Lignes de facture</label>
            <div className="space-y-3">
              {formData.lignes.map((ligne, index) => (
                <div key={index} className="flex gap-2 items-start p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1 grid grid-cols-5 gap-2">
                    <input
                      type="text"
                      value={ligne.description}
                      onChange={(e) => handleLigneChange(index, 'description', e.target.value)}
                      placeholder="Description"
                      className="col-span-2 px-2 py-1.5 border border-gray-200 rounded text-sm"
                    />
                    <input
                      type="number"
                      value={ligne.quantite}
                      onChange={(e) => handleLigneChange(index, 'quantite', parseFloat(e.target.value) || 0)}
                      placeholder="Qté"
                      className="px-2 py-1.5 border border-gray-200 rounded text-sm"
                      min="0"
                      step="1"
                    />
                    <input
                      type="number"
                      value={ligne.prix_unitaire_ht}
                      onChange={(e) => handleLigneChange(index, 'prix_unitaire_ht', parseFloat(e.target.value) || 0)}
                      placeholder="Prix HT"
                      className="px-2 py-1.5 border border-gray-200 rounded text-sm"
                      min="0"
                      step="0.01"
                    />
                    <select
                      value={ligne.categorie}
                      onChange={(e) => handleLigneChange(index, 'categorie', e.target.value)}
                      className="px-2 py-1.5 border border-gray-200 rounded text-sm"
                    >
                      {categories.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  {formData.lignes.length > 1 && (
                    <button
                      onClick={() => handleRemoveLigne(index)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <button
              onClick={handleAddLigne}
              className="mt-2 text-sm text-[#00D3A8] hover:underline"
            >
              + Ajouter une ligne
            </button>
          </div>

          {/* Totaux */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Total HT</span>
              <span>{totalHT.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between text-sm mb-1">
              <span>TVA</span>
              <span>{totalTVA.toFixed(2)} €</span>
            </div>
            <div className="flex justify-between font-semibold text-lg pt-2 border-t border-gray-200">
              <span>Total TTC</span>
              <span>{totalTTC.toFixed(2)} €</span>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-gray-600 hover:text-gray-800">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-[#00D3A8] text-white rounded-lg hover:bg-[#00BFA6] disabled:opacity-50"
          >
            {saving ? 'Création...' : 'Créer la facture'}
          </button>
        </div>
      </div>

      {/* New Fournisseur Mini Modal */}
      {showNewFournisseur && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-xl p-6 w-96">
            <h4 className="font-semibold mb-4">Nouveau fournisseur</h4>
            <div className="space-y-3">
              <input
                type="text"
                value={newFournisseur.nom}
                onChange={(e) => setNewFournisseur({ ...newFournisseur, nom: e.target.value })}
                placeholder="Nom *"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
              <input
                type="email"
                value={newFournisseur.email}
                onChange={(e) => setNewFournisseur({ ...newFournisseur, email: e.target.value })}
                placeholder="Email"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
              <input
                type="tel"
                value={newFournisseur.telephone}
                onChange={(e) => setNewFournisseur({ ...newFournisseur, telephone: e.target.value })}
                placeholder="Téléphone"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg"
              />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowNewFournisseur(false)} className="px-3 py-1.5 text-gray-600">
                Annuler
              </button>
              <button
                onClick={handleCreateFournisseur}
                className="px-3 py-1.5 bg-[#00D3A8] text-white rounded-lg"
              >
                Créer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Facture Detail Modal
function FactureDetailModal({ facture, onClose }) {
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl">
        <div className="p-6 border-b border-gray-100 flex justify-between items-start">
          <div>
            <h3 className="text-lg font-semibold">Facture {facture.numero}</h3>
            <p className="text-sm text-gray-500">{facture.fournisseur_nom}</p>
          </div>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${statusColors[facture.statut]}`}>
            {statusLabels[facture.statut]}
          </span>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-500">Date facture</p>
              <p className="font-medium">{new Date(facture.date_facture).toLocaleDateString('fr-FR')}</p>
            </div>
            <div>
              <p className="text-gray-500">Référence fournisseur</p>
              <p className="font-medium">{facture.numero_fournisseur || '-'}</p>
            </div>
            <div>
              <p className="text-gray-500">Date paiement</p>
              <p className="font-medium">
                {facture.date_paiement ? new Date(facture.date_paiement).toLocaleDateString('fr-FR') : '-'}
              </p>
            </div>
          </div>

          {/* Lignes */}
          <div>
            <h4 className="font-medium mb-2">Détail</h4>
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Qté</th>
                  <th className="px-3 py-2 text-right">P.U. HT</th>
                  <th className="px-3 py-2 text-right">Total TTC</th>
                </tr>
              </thead>
              <tbody>
                {facture.lignes?.map((ligne, i) => (
                  <tr key={i} className="border-b border-gray-100">
                    <td className="px-3 py-2">{ligne.description}</td>
                    <td className="px-3 py-2 text-right">{ligne.quantite}</td>
                    <td className="px-3 py-2 text-right">{formatMoney(ligne.prix_unitaire_ht)}</td>
                    <td className="px-3 py-2 text-right">{formatMoney(ligne.montant_ttc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totaux */}
          <div className="flex justify-end">
            <div className="w-48 space-y-1 text-sm">
              <div className="flex justify-between">
                <span>Total HT</span>
                <span>{formatMoney(facture.montant_ht)}</span>
              </div>
              <div className="flex justify-between">
                <span>TVA</span>
                <span>{formatMoney(facture.montant_tva)}</span>
              </div>
              <div className="flex justify-between font-semibold text-base pt-1 border-t">
                <span>Total TTC</span>
                <span>{formatMoney(facture.montant_ttc)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200">
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}
